import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext.jsx';
import { Send, Phone } from 'lucide-react';
import { API_BASE_URL } from '../utils/network.js';
import './SupportChatPanel.css';

// The backend's replies come back as plain text containing lightweight Markdown
// (mostly **bold** and "- "/"* " bullet lists) — see server.js's support-chat system
// prompt. Pulling in a full Markdown library (e.g. react-markdown) for that would be
// overkill, so this is a small regex-based renderer covering just those two cases.
// Only ever applied to the assistant's own messages — a user's typed text is shown as-is.
function renderAssistantMarkdown(text) {
  const lines = String(text ?? '').split('\n');
  const nodes = [];
  let listItems = [];

  const renderInline = (line, keyPrefix) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((part, i) => {
      const boldMatch = /^\*\*([^*]+)\*\*$/.exec(part);
      return boldMatch
        ? <strong key={`${keyPrefix}-${i}`}>{boldMatch[1]}</strong>
        : <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
    });
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`} style={{ margin: '4px 0', paddingLeft: 18 }}>
        {listItems.map((item, i) => <li key={i}>{renderInline(item, `li-${nodes.length}-${i}`)}</li>)}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((line, idx) => {
    const bulletMatch = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bulletMatch) {
      listItems.push(bulletMatch[1]);
      return;
    }
    flushList();
    if (line.trim() === '') {
      // Preserve blank lines as spacing between paragraphs, but collapse a leading
      // one (nothing to space out from yet).
      if (nodes.length > 0) nodes.push(<br key={`br-${idx}`} />);
      return;
    }
    nodes.push(<React.Fragment key={`ln-${idx}`}>{renderInline(line, `ln-${idx}`)}<br /></React.Fragment>);
  });
  flushList();

  return nodes;
}

// AI support chat, embedded inline in Settings (not a global floating widget - it only
// shows up when the user goes looking for it). The conversation lives only in this
// component's state - nothing is persisted server-side or reloaded on refresh, keeping
// the backend endpoint (POST /api/support-chat) stateless. The site's current UI
// language (i18n.language) is sent with every message so replies match it, rather than
// guessing from whatever language the user happens to type in.
// A separate "leave your contact" form (POST /api/support-chat/contact-request) is how a
// user actually reaches a human - real name/contact info goes there, not into the AI chat.
export default function SupportChatPanel() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [contactNote, setContactNote] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, showContactForm]);

  useEffect(() => {
    if (user && !contactName) {
      setContactName([user.name, user.surname].filter(Boolean).join(' ') || user.username || '');
    }
    if (user && !contactInfo) {
      setContactInfo(user.email || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setError('');
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/support-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, history: nextMessages, lang: i18n.language?.split('-')[0] }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setError(t('supportChat.error'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const submitContactRequest = async () => {
    if (!contactName.trim() || !contactInfo.trim() || contactSending) return;
    setContactSending(true);
    setContactError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/support-chat/contact-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ name: contactName.trim(), contact: contactInfo.trim(), note: contactNote.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setContactSent(true);
      setContactNote('');
    } catch (e) {
      setContactError(t('supportChat.error'));
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="sc-panel sc-panel-inline">
      {!showContactForm ? (
        <>
          <div className="sc-messages" ref={listRef}>
            {messages.length === 0 && (
              <div className="sc-message sc-message-assistant">{t('supportChat.welcome')}</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`sc-message sc-message-${m.role}`}>
                {m.role === 'assistant' ? renderAssistantMarkdown(m.content) : m.content}
              </div>
            ))}
            {sending && (
              <div className="sc-message sc-message-assistant sc-typing">
                <span></span><span></span><span></span>
              </div>
            )}
            {error && <div className="sc-error">{error}</div>}
          </div>
          <button className="sc-contact-link" onClick={() => { setShowContactForm(true); setContactSent(false); }}>
            <Phone size={13} /> {t('supportChat.leaveContact')}
          </button>
          <div className="sc-input-row">
            <textarea
              className="sc-input"
              rows={1}
              placeholder={t('supportChat.placeholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="sc-send-btn" onClick={send} disabled={sending || !input.trim()} aria-label="Send">
              <Send size={18} />
            </button>
          </div>
        </>
      ) : (
        <div className="sc-contact-form">
          {contactSent ? (
            <div className="sc-contact-success">{t('supportChat.contactSent')}</div>
          ) : (
            <>
              <p className="sc-contact-intro">{t('supportChat.contactIntro')}</p>
              <input
                className="sc-contact-input"
                placeholder={t('supportChat.contactNamePlaceholder')}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
              <input
                className="sc-contact-input"
                placeholder={t('supportChat.contactInfoPlaceholder')}
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
              />
              <textarea
                className="sc-contact-input sc-contact-textarea"
                rows={3}
                placeholder={t('supportChat.contactNotePlaceholder')}
                value={contactNote}
                onChange={(e) => setContactNote(e.target.value)}
              />
              {contactError && <div className="sc-error">{contactError}</div>}
              <button
                className="sc-contact-submit"
                onClick={submitContactRequest}
                disabled={contactSending || !contactName.trim() || !contactInfo.trim()}
              >
                {contactSending ? t('supportChat.contactSending') : t('supportChat.contactSubmit')}
              </button>
            </>
          )}
          <button className="sc-contact-back" onClick={() => setShowContactForm(false)}>
            {t('supportChat.backToChat')}
          </button>
        </div>
      )}
    </div>
  );
}
